import React, { useEffect, useState } from 'react';
import { Maximize2, Sparkles, Send, Info, Edit2, X, Check, Search, Loader2, ArrowRight, MapPin, TrendingUp, Users, BarChart3, ArrowLeftRight, Eye, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { LensVersion, LensData } from '../types';
import { CAREER_LEVELS, RECENCY_TRIGGERS, US_STATES } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
type SuggestionFlow = 1 | 2 | 3 | 4 | 5;
type ExplorationState = 'idle' | 'loading' | 'chatting' | 'preview' | 'comparing' | 'exploring';
interface SuggestionConfig {
  label: string;
  name: string;
  region: string;
  newData: (base: LensData) => LensData;
}
const SUGGESTIONS: SuggestionConfig[] = [{
  label: 'Explore Texas opportunity',
  name: 'Texas Deep Dive',
  region: 'Texas',
  newData: base => ({
    ...base,
    states: ['Texas']
  })
}, {
  label: 'Compare with Ultra Wealth ($10M+)',
  name: 'Ultra Wealth ($10M+)',
  region: 'All',
  newData: base => ({
    ...base,
    netWorth: {
      min: '$10M',
      max: ''
    }
  })
}, {
  label: 'Focus on underpenetrated regions',
  name: 'Underpenetrated Regions',
  region: 'OH, PA, IL',
  newData: base => ({
    ...base,
    states: ['Ohio', 'Pennsylvania', 'Illinois']
  })
}];
const MOCK_INSIGHTS = {
  Texas: {
    summary: 'Texas shows significant untapped potential with only 0.2% penetration despite 220K total households matching your criteria.',
    metrics: [{
      label: 'TAM Households',
      value: '220,000',
      trend: '+12% YoY'
    }, {
      label: 'Current Customers',
      value: '440',
      trend: null
    }, {
      label: 'Penetration',
      value: '0.2%',
      trend: 'vs 4.9% avg'
    }, {
      label: 'Avg Net Worth',
      value: '$95.4M',
      trend: '+8% YoY'
    }],
    recommendation: 'Texas represents your largest whitespace opportunity. The Dallas-Fort Worth and Austin metros show the highest concentration of target profiles.'
  },
  All: {
    summary: 'Ultra-high-net-worth individuals ($10M+) represent a smaller but significantly more valuable segment.',
    metrics: [{
      label: 'TAM Households',
      value: '85,000',
      trend: '-82% vs current'
    }, {
      label: 'Current Customers',
      value: '2,100',
      trend: null
    }, {
      label: 'Penetration',
      value: '2.5%',
      trend: 'vs 1.25% avg'
    }, {
      label: 'Avg Net Worth',
      value: '$24.8M',
      trend: '+15% YoY'
    }],
    recommendation: 'Your penetration is actually higher in this segment, suggesting strong product-market fit. Focus on geographic expansion.'
  },
  'OH, PA, IL': {
    summary: 'These three states have penetration rates below 3.5%, well under your 4.9% national average.',
    metrics: [{
      label: 'TAM Households',
      value: '302,000',
      trend: 'Combined'
    }, {
      label: 'Current Customers',
      value: '11,114',
      trend: null
    }, {
      label: 'Avg Penetration',
      value: '3.5%',
      trend: 'vs 4.9% avg'
    }, {
      label: 'Opportunity Gap',
      value: '~4,200',
      trend: 'households'
    }],
    recommendation: 'Ohio and Pennsylvania metros like Columbus, Pittsburgh, and Philadelphia have the highest density of unmatched prospects.'
  }
};
export function CopilotSidebar({
  activeLens,
  onUpdateLens,
  onCreateNewVersion
}: {
  activeLens?: LensVersion | null;
  onUpdateLens: (id: string, data: LensData) => void;
  onCreateNewVersion: (name: string, data: LensData) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<LensData | null>(null);
  const [stateSearch, setStateSearch] = useState('');
  // Suggestion flow state
  const [flowSolution, setFlowSolution] = useState<SuggestionFlow>(1);
  const [explorationState, setExplorationState] = useState<ExplorationState>('idle');
  const [activeSuggestion, setActiveSuggestion] = useState<SuggestionConfig | null>(null);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{
    role: 'user' | 'ai';
    content: string;
  }>>([]);
  useEffect(() => {
    if (activeLens && !isEditing) {
      setEditData(activeLens.lensData);
    }
  }, [activeLens, isEditing]);
  const handleSave = () => {
    if (!activeLens || !editData) return;
    setIsSaving(true);
    setTimeout(() => {
      onUpdateLens(activeLens.id, editData);
      setIsSaving(false);
      setIsEditing(false);
    }, 600);
  };
  const handleCancel = () => {
    setIsEditing(false);
    if (activeLens) setEditData(activeLens.lensData);
  };
  const resetExploration = () => {
    setExplorationState('idle');
    setActiveSuggestion(null);
    setSummaryCollapsed(false);
    setChatMessages([]);
  };
  const handleSuggestionClick = (suggestion: SuggestionConfig) => {
    if (!activeLens) return;
    setActiveSuggestion(suggestion);
    switch (flowSolution) {
      case 1:
        // Instant Version + AI Response
        setExplorationState('loading');
        setSummaryCollapsed(true);
        setTimeout(() => {
          onCreateNewVersion(suggestion.name, suggestion.newData(activeLens.lensData));
          setChatMessages([{
            role: 'user',
            content: suggestion.label
          }, {
            role: 'ai',
            content: (MOCK_INSIGHTS as any)[suggestion.region]?.recommendation || 'Analysis complete.'
          }]);
          setExplorationState('chatting');
        }, 1200);
        break;
      case 2:
        // Chat-First → Generate
        setExplorationState('loading');
        setSummaryCollapsed(true);
        setChatMessages([{
          role: 'user',
          content: suggestion.label
        }]);
        setTimeout(() => {
          setChatMessages(prev => [...prev, {
            role: 'ai',
            content: (MOCK_INSIGHTS as any)[suggestion.region]?.summary + '\n\n' + (MOCK_INSIGHTS as any)[suggestion.region]?.recommendation
          }]);
          setExplorationState('chatting');
        }, 1500);
        break;
      case 3:
        // Preview Card
        setExplorationState('loading');
        setTimeout(() => {
          setExplorationState('preview');
        }, 800);
        break;
      case 4:
        // Side-by-Side Comparison
        setExplorationState('loading');
        setTimeout(() => {
          setExplorationState('comparing');
        }, 800);
        break;
      case 5:
        // Temporary Exploration
        setExplorationState('exploring');
        break;
    }
  };
  const handleConfirmGenerate = () => {
    if (!activeLens || !activeSuggestion) return;
    setExplorationState('loading');
    setTimeout(() => {
      onCreateNewVersion(activeSuggestion.name, activeSuggestion.newData(activeLens.lensData));
      resetExploration();
    }, 600);
  };
  const toggleArrayItem = (array: string[], item: string) => {
    return array.includes(item) ? array.filter(i => i !== item) : [...array, item];
  };
  const filteredStates = US_STATES.filter(s => s.toLowerCase().includes(stateSearch.toLowerCase()));
  const insights = activeSuggestion ? (MOCK_INSIGHTS as any)[activeSuggestion.region] : null;
  const renderSummaryCard = () => {
    if (explorationState !== 'idle' && summaryCollapsed) {
      return <button onClick={() => setSummaryCollapsed(false)} className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 mb-4 flex items-center justify-between w-full text-left shadow-sm hover:bg-gray-50 transition-colors" data-id="element-101">
          <div className="flex items-center space-x-2" data-id="element-102">
            <div className="w-4 h-4 bg-teal-500 rounded flex items-center justify-center text-white text-[8px] font-bold" data-id="element-103">
              L
            </div>
            <span className="text-xs font-medium text-gray-700 truncate" data-id="element-104">
              {activeLens?.lensData.netWorth.min || '$0'} NW •{' '}
              {activeLens?.lensData.states?.join(', ') || 'All'}
            </span>
          </div>
          <ChevronDown size={12} className="text-gray-400" data-id="element-105" />
        </button>;
    }
    return <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 overflow-hidden" data-id="element-106">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50" data-id="element-107">
          <h4 className="font-semibold text-gray-900 text-base" data-id="element-108">
            Market Lens Summary
          </h4>
          <div className="flex items-center space-x-1" data-id="element-109">
            {explorationState !== 'idle' && <button onClick={() => setSummaryCollapsed(true)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" data-id="element-110">
                <ChevronUp size={14} data-id="element-111" />
              </button>}
            {!isEditing && <button onClick={() => setIsEditing(true)} className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors" title="Edit Lens" data-id="element-112">
                <Edit2 size={14} data-id="element-113" />
              </button>}
          </div>
        </div>

        <div className="p-5" data-id="element-114">
          <AnimatePresence mode="wait" data-id="element-115">
            {isEditing && editData ? <motion.div key="edit" initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -5
          }} className="space-y-5" data-id="element-116">
                <div className="space-y-3" data-id="element-117">
                  <div data-id="element-118">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-119">
                      Net Worth
                    </label>
                    <div className="flex space-x-2" data-id="element-120">
                      <input type="text" value={editData.netWorth.min} onChange={e => setEditData({
                    ...editData,
                    netWorth: {
                      ...editData.netWorth,
                      min: e.target.value
                    }
                  })} placeholder="Min" className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-500" data-id="element-121" />
                      <input type="text" value={editData.netWorth.max} onChange={e => setEditData({
                    ...editData,
                    netWorth: {
                      ...editData.netWorth,
                      max: e.target.value
                    }
                  })} placeholder="Max" className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-500" data-id="element-122" />
                    </div>
                  </div>
                  <div data-id="element-123">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-124">
                      Investable Assets
                    </label>
                    <div className="flex space-x-2" data-id="element-125">
                      <input type="text" value={editData.assets.min} onChange={e => setEditData({
                    ...editData,
                    assets: {
                      ...editData.assets,
                      min: e.target.value
                    }
                  })} placeholder="Min" className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-500" data-id="element-126" />
                      <input type="text" value={editData.assets.max} onChange={e => setEditData({
                    ...editData,
                    assets: {
                      ...editData.assets,
                      max: e.target.value
                    }
                  })} placeholder="Max" className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-teal-500" data-id="element-127" />
                    </div>
                  </div>
                  <div data-id="element-128">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-129">
                      Career Levels
                    </label>
                    <div className="flex flex-wrap gap-1.5" data-id="element-130">
                      {CAREER_LEVELS.map(level => <button key={level} onClick={() => setEditData({
                    ...editData,
                    careers: toggleArrayItem(editData.careers, level)
                  })} className={`px-2 py-1 rounded text-[11px] font-medium border transition-colors ${editData.careers.includes(level) ? 'bg-teal-50 border-teal-500 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300'}`} data-id="element-131">
                          {level}
                        </button>)}
                    </div>
                  </div>
                  <div data-id="element-132">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-133">
                      Recency Triggers
                    </label>
                    <div className="flex flex-wrap gap-1.5" data-id="element-134">
                      {RECENCY_TRIGGERS.map(trigger => <button key={trigger} onClick={() => setEditData({
                    ...editData,
                    recency: toggleArrayItem(editData.recency, trigger)
                  })} className={`px-2 py-1 rounded text-[11px] font-medium border transition-colors ${editData.recency.includes(trigger) ? 'bg-teal-50 border-teal-500 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300'}`} data-id="element-135">
                          {trigger}
                        </button>)}
                    </div>
                  </div>
                  <div data-id="element-136">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5" data-id="element-137">
                      States
                    </label>
                    <div className="relative mb-2" data-id="element-138">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={12} data-id="element-139" />
                      <input type="text" value={stateSearch} onChange={e => setStateSearch(e.target.value)} placeholder="Search states..." className="w-full border border-gray-200 rounded-md pl-6 pr-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" data-id="element-140" />
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-0.5" data-id="element-141">
                      {filteredStates.map(state => <button key={state} onClick={() => setEditData({
                    ...editData,
                    states: toggleArrayItem(editData.states, state)
                  })} className={`px-2 py-1 rounded text-[11px] font-medium border transition-colors ${editData.states.includes(state) ? 'bg-teal-50 border-teal-500 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300'}`} data-id="element-142">
                          {state}
                        </button>)}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 pt-2 border-t border-gray-100" data-id="element-143">
                  <button onClick={handleCancel} disabled={isSaving} className="flex-1 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50" data-id="element-144">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={isSaving} className="flex-1 px-3 py-1.5 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 transition-colors flex items-center justify-center disabled:opacity-70" data-id="element-145">
                    {isSaving ? <Loader2 size={14} className="animate-spin" data-id="element-146" /> : 'Apply Changes'}
                  </button>
                </div>
              </motion.div> : <motion.div key="display" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="space-y-4 text-sm" data-id="element-147">
                <div className="flex justify-between items-baseline group p-1 -mx-1 rounded hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setIsEditing(true)} data-id="element-148">
                  <span className="text-gray-500" data-id="element-149">Net Worth</span>
                  <span className="font-medium text-gray-900" data-id="element-150">
                    {activeLens?.lensData.netWorth.min || '$0'} –{' '}
                    {activeLens?.lensData.netWorth.max || 'Any'}
                  </span>
                </div>
                <div className="flex justify-between items-baseline group p-1 -mx-1 rounded hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setIsEditing(true)} data-id="element-151">
                  <span className="text-gray-500" data-id="element-152">Investable Assets</span>
                  <span className="font-medium text-gray-900" data-id="element-153">
                    {activeLens?.lensData.assets.min || '$0'} –{' '}
                    {activeLens?.lensData.assets.max || 'Any'}
                  </span>
                </div>
                <div className="group p-1 -mx-1 rounded hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setIsEditing(true)} data-id="element-154">
                  <span className="text-gray-500 block mb-1" data-id="element-155">
                    Career Levels
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-156">
                    {activeLens?.lensData.careers && activeLens.lensData.careers.length > 0 ? activeLens.lensData.careers.join(', ') : 'Any'}
                  </span>
                </div>
                <div className="group p-1 -mx-1 rounded hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setIsEditing(true)} data-id="element-157">
                  <span className="text-gray-500 block mb-1" data-id="element-158">
                    Recency Triggers
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-159">
                    {activeLens?.lensData.recency && activeLens.lensData.recency.length > 0 ? activeLens.lensData.recency.join(', ') : 'None'}
                  </span>
                </div>
                <div className="group p-1 -mx-1 rounded hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setIsEditing(true)} data-id="element-160">
                  <span className="text-gray-500 block mb-1" data-id="element-161">States</span>
                  <span className="font-medium text-gray-900" data-id="element-162">
                    {activeLens?.lensData.states && activeLens.lensData.states.length > 0 ? activeLens.lensData.states.join(', ') : 'All'}
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>;
  };
  // Render the exploration/suggestion interaction area based on active solution
  const renderExplorationArea = () => {
    if (explorationState === 'loading') {
      return <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col items-center py-8" data-id="element-163">
          <Loader2 size={24} className="animate-spin text-teal-500 mb-3" data-id="element-164" />
          <p className="text-sm text-gray-500" data-id="element-165">
            Analyzing {activeSuggestion?.region || 'data'}...
          </p>
        </motion.div>;
    }
    // Solution 1: Instant Version + AI Chat Response
    if (flowSolution === 1 && explorationState === 'chatting') {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-3" data-id="element-166">
          <div className="flex items-center justify-between mb-2" data-id="element-167">
            <span className="text-xs font-semibold text-teal-600 flex items-center" data-id="element-168">
              <Check size={12} className="mr-1" data-id="element-169" /> New lens created
            </span>
            <button onClick={resetExploration} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-170">
              <X size={14} data-id="element-171" />
            </button>
          </div>
          {chatMessages.map((msg, i) => <div key={i} className={`${msg.role === 'user' ? 'ml-6' : ''}`} data-id="element-172">
              <div className={`px-3 py-2.5 rounded-xl text-xs leading-relaxed ${msg.role === 'user' ? 'bg-teal-500 text-white rounded-tr-sm ml-auto' : 'bg-gray-50 text-gray-700 rounded-tl-sm border border-gray-100'}`} data-id="element-173">
                {msg.content}
              </div>
            </div>)}
          <div className="pt-2 border-t border-gray-100" data-id="element-174">
            <p className="text-[10px] text-gray-400 text-center" data-id="element-175">
              Dashboard updated with "{activeSuggestion?.name}" lens
            </p>
          </div>
        </motion.div>;
    }
    // Solution 2: Chat-First → Generate Button
    if (flowSolution === 2 && explorationState === 'chatting') {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-3" data-id="element-176">
          <div className="flex items-center justify-between mb-1" data-id="element-177">
            <span className="text-xs font-medium text-gray-500" data-id="element-178">
              AI Analysis
            </span>
            <button onClick={resetExploration} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-179">
              <X size={14} data-id="element-180" />
            </button>
          </div>
          {chatMessages.map((msg, i) => <div key={i} data-id="element-181">
              <div className={`px-3 py-2.5 rounded-xl text-xs leading-relaxed ${msg.role === 'user' ? 'bg-teal-500 text-white rounded-tr-sm ml-6' : 'bg-gray-50 text-gray-700 rounded-tl-sm border border-gray-100'}`} data-id="element-182">
                {msg.content}
              </div>
            </div>)}
          {insights && <div className="grid grid-cols-2 gap-2 mt-3" data-id="element-183">
              {insights.metrics.map((m: any, i: number) => <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100" data-id="element-184">
                  <p className="text-[10px] text-gray-500" data-id="element-185">{m.label}</p>
                  <p className="text-sm font-bold text-gray-900" data-id="element-186">{m.value}</p>
                  {m.trend && <p className="text-[10px] text-teal-600" data-id="element-187">{m.trend}</p>}
                </div>)}
            </div>}
          <div className="flex space-x-2 pt-3" data-id="element-188">
            <button onClick={resetExploration} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors" data-id="element-189">
              Back
            </button>
            <button onClick={handleConfirmGenerate} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center justify-center" data-id="element-190">
              <Save size={12} className="mr-1.5" data-id="element-191" />
              Generate New Lens
            </button>
          </div>
        </motion.div>;
    }
    // Solution 3: Preview Card with Confirm
    if (flowSolution === 3 && explorationState === 'preview') {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-192">
          <div className="flex items-center justify-between" data-id="element-193">
            <span className="text-xs font-semibold text-gray-700" data-id="element-194">
              Preview: {activeSuggestion?.name}
            </span>
            <button onClick={resetExploration} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-195">
              <X size={14} data-id="element-196" />
            </button>
          </div>

          <div className="bg-teal-50 border border-teal-100 rounded-xl p-4" data-id="element-197">
            <div className="flex items-center space-x-2 mb-3" data-id="element-198">
              <MapPin size={14} className="text-teal-600" data-id="element-199" />
              <span className="text-sm font-semibold text-teal-800" data-id="element-200">
                {activeSuggestion?.region}
              </span>
            </div>
            <p className="text-xs text-teal-700 leading-relaxed mb-3" data-id="element-201">
              {insights?.summary}
            </p>

            {insights && <div className="grid grid-cols-2 gap-2" data-id="element-202">
                {insights.metrics.slice(0, 4).map((m: any, i: number) => <div key={i} className="bg-white/80 rounded-lg p-2 border border-teal-100" data-id="element-203">
                    <p className="text-[10px] text-teal-600" data-id="element-204">{m.label}</p>
                    <p className="text-sm font-bold text-gray-900" data-id="element-205">{m.value}</p>
                  </div>)}
              </div>}
          </div>

          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-206">
            <p className="text-[10px] text-gray-500 mb-1" data-id="element-207">What will change:</p>
            <div className="flex items-center space-x-2 text-xs" data-id="element-208">
              <span className="text-gray-500 line-through" data-id="element-209">
                {activeLens?.lensData.states?.join(', ') || 'All States'}
              </span>
              <ArrowRight size={10} className="text-gray-400" data-id="element-210" />
              <span className="font-medium text-teal-700" data-id="element-211">
                {activeSuggestion?.region}
              </span>
            </div>
          </div>

          <div className="flex space-x-2" data-id="element-212">
            <button onClick={resetExploration} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors" data-id="element-213">
              Dismiss
            </button>
            <button onClick={handleConfirmGenerate} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center justify-center" data-id="element-214">
              <Save size={12} className="mr-1.5" data-id="element-215" />
              Save as New Lens
            </button>
          </div>
        </motion.div>;
    }
    // Solution 4: Side-by-Side Comparison
    if (flowSolution === 4 && explorationState === 'comparing') {
      const currentStates = activeLens?.lensData.states?.join(', ') || 'All';
      const newStates = activeSuggestion?.region || 'All';
      const currentNW = activeLens?.lensData.netWorth.min || '$0';
      const newNW = activeSuggestion?.newData(activeLens!.lensData).netWorth.min || currentNW;
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-3" data-id="element-216">
          <div className="flex items-center justify-between" data-id="element-217">
            <span className="text-xs font-semibold text-gray-700 flex items-center" data-id="element-218">
              <ArrowLeftRight size={12} className="mr-1.5 text-indigo-500" data-id="element-219" />
              Comparison
            </span>
            <button onClick={resetExploration} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-220">
              <X size={14} data-id="element-221" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2" data-id="element-222">
            <div className="text-center" data-id="element-223">
              <p className="text-[10px] font-medium text-gray-400 mb-2 uppercase tracking-wide" data-id="element-224">
                Current
              </p>
            </div>
            <div className="text-center" data-id="element-225">
              <p className="text-[10px] font-medium text-teal-500 mb-2 uppercase tracking-wide" data-id="element-226">
                Proposed
              </p>
            </div>
          </div>

          {/* Comparison rows */}
          <div className="space-y-2" data-id="element-227">
            <div className="grid grid-cols-2 gap-2" data-id="element-228">
              <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100 text-center" data-id="element-229">
                <p className="text-[10px] text-gray-500" data-id="element-230">States</p>
                <p className="text-xs font-medium text-gray-700 truncate" data-id="element-231">
                  {currentStates}
                </p>
              </div>
              <div className="bg-teal-50 rounded-lg p-2.5 border border-teal-100 text-center" data-id="element-232">
                <p className="text-[10px] text-teal-600" data-id="element-233">States</p>
                <p className="text-xs font-medium text-teal-800 truncate" data-id="element-234">
                  {newStates}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2" data-id="element-235">
              <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100 text-center" data-id="element-236">
                <p className="text-[10px] text-gray-500" data-id="element-237">Net Worth</p>
                <p className="text-xs font-medium text-gray-700" data-id="element-238">
                  {currentNW}+
                </p>
              </div>
              <div className="bg-teal-50 rounded-lg p-2.5 border border-teal-100 text-center" data-id="element-239">
                <p className="text-[10px] text-teal-600" data-id="element-240">Net Worth</p>
                <p className="text-xs font-medium text-teal-800" data-id="element-241">{newNW}+</p>
              </div>
            </div>
            {insights && <div className="grid grid-cols-2 gap-2" data-id="element-242">
                <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100 text-center" data-id="element-243">
                  <p className="text-[10px] text-gray-500" data-id="element-244">TAM</p>
                  <p className="text-xs font-medium text-gray-700" data-id="element-245">1.45M</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-2.5 border border-teal-100 text-center" data-id="element-246">
                  <p className="text-[10px] text-teal-600" data-id="element-247">TAM</p>
                  <p className="text-xs font-medium text-teal-800" data-id="element-248">
                    {insights.metrics[0].value}
                  </p>
                </div>
              </div>}
          </div>

          <div className="flex space-x-2 pt-2" data-id="element-249">
            <button onClick={resetExploration} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors" data-id="element-250">
              Cancel
            </button>
            <button onClick={handleConfirmGenerate} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center justify-center" data-id="element-251">
              <Save size={12} className="mr-1.5" data-id="element-252" />
              Create Version
            </button>
          </div>
        </motion.div>;
    }
    // Solution 5: Temporary Exploration Mode
    if (flowSolution === 5 && explorationState === 'exploring') {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-3" data-id="element-253">
          {/* Exploration banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-id="element-254">
            <div className="flex items-center justify-between mb-2" data-id="element-255">
              <div className="flex items-center space-x-2" data-id="element-256">
                <Eye size={14} className="text-amber-600" data-id="element-257" />
                <span className="text-xs font-semibold text-amber-800" data-id="element-258">
                  Exploring: {activeSuggestion?.region}
                </span>
              </div>
              <button onClick={resetExploration} className="text-xs text-amber-600 hover:text-amber-800 font-medium" data-id="element-259">
                Exit
              </button>
            </div>
            <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-260">
              You're previewing this region. Dashboard is temporarily filtered.
              No changes saved yet.
            </p>
          </div>

          {/* Insights */}
          {insights && <>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-261">
                {insights.summary}
              </p>
              <div className="grid grid-cols-2 gap-2" data-id="element-262">
                {insights.metrics.map((m: any, i: number) => <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100" data-id="element-263">
                    <p className="text-[10px] text-gray-500" data-id="element-264">{m.label}</p>
                    <p className="text-sm font-bold text-gray-900" data-id="element-265">{m.value}</p>
                    {m.trend && <p className="text-[10px] text-teal-600" data-id="element-266">{m.trend}</p>}
                  </div>)}
              </div>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-267">
                {insights.recommendation}
              </p>
            </>}

          {/* What would change */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-268">
            <p className="text-[10px] font-medium text-gray-500 mb-1.5" data-id="element-269">
              Changes if saved:
            </p>
            <div className="flex items-center space-x-2 text-xs" data-id="element-270">
              <span className="text-gray-400 line-through" data-id="element-271">
                {activeLens?.lensData.states?.join(', ') || 'All'}
              </span>
              <ArrowRight size={10} className="text-gray-400" data-id="element-272" />
              <span className="font-medium text-teal-700" data-id="element-273">
                {activeSuggestion?.region}
              </span>
            </div>
          </div>

          <div className="flex space-x-2 pt-1" data-id="element-274">
            <button onClick={resetExploration} className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors" data-id="element-275">
              Exit Exploration
            </button>
            <button onClick={handleConfirmGenerate} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors flex items-center justify-center" data-id="element-276">
              <Save size={12} className="mr-1.5" data-id="element-277" />
              Save as New Lens
            </button>
          </div>
        </motion.div>;
    }
    // Default: show suggestions
    return <>
        <h3 className="text-teal-600 font-semibold text-sm mb-2 text-center" data-id="element-278">
          What do you want to know about this Market Lens?
        </h3>
        <p className="text-gray-500 text-xs mb-4 text-center" data-id="element-279">
          I can help you uncover where you're strongest, where you're
          underpenetrated, and where to expand next.
        </p>
        <div className="w-full space-y-2.5" data-id="element-280">
          {SUGGESTIONS.map((suggestion, i) => <button key={i} onClick={() => handleSuggestionClick(suggestion)} className="w-full bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-gray-700 text-sm py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" data-id="element-281">
              <Sparkles size={14} className="text-teal-500 mr-2 shrink-0" data-id="element-282" />
              {suggestion.label}
            </button>)}
        </div>
      </>;
  };
  return <div className="w-[340px] border-l border-gray-200 bg-white flex flex-col h-full shrink-0" data-id="element-283">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" data-id="element-284">
        <div className="flex items-center space-x-2" data-id="element-285">
          <div className="w-5 h-5 bg-teal-500 rounded flex items-center justify-center text-white font-bold text-xs" data-id="element-286">
            W
          </div>
          <h2 className="font-semibold text-gray-900 text-sm" data-id="element-287">
            Windfall AI Copilot
          </h2>
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded-full" data-id="element-288">
            Beta
          </span>
        </div>
        <button className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-md transition-colors" data-id="element-289">
          <Maximize2 size={14} data-id="element-290" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5" data-id="element-291">
        {/* Solution Picker */}
        <div className="flex items-center gap-1.5 mb-4 p-2.5 bg-violet-50/70 border border-violet-100 rounded-lg" data-id="element-292">
          <span className="text-[10px] font-semibold text-violet-600 mr-1 whitespace-nowrap" data-id="element-293">
            🧪 Flow:
          </span>
          {([1, 2, 3, 4, 5] as const).map(n => <button key={n} onClick={() => {
          setFlowSolution(n);
          resetExploration();
        }} className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${flowSolution === n ? 'bg-violet-600 text-white' : 'bg-white text-violet-600 border border-violet-200 hover:bg-violet-100'}`} data-id="element-294">
              {n}
            </button>)}
        </div>
        <div className="mb-4 text-[10px] text-violet-400 text-center" data-id="element-295">
          {flowSolution === 1 && 'Instant Version + AI Response'}
          {flowSolution === 2 && 'Chat-First → Generate Button'}
          {flowSolution === 3 && 'Preview Card + Confirm'}
          {flowSolution === 4 && 'Side-by-Side Comparison'}
          {flowSolution === 5 && 'Temporary Exploration Mode'}
        </div>

        {renderSummaryCard()}

        <AnimatePresence mode="wait" data-id="element-296">
          <motion.div key={explorationState + flowSolution} data-id="element-297">
            {renderExplorationArea()}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="p-4 bg-white border-t border-gray-100" data-id="element-298">
        <div className="relative flex items-center" data-id="element-299">
          <input type="text" placeholder="Ask anything..." className="w-full border border-teal-500 rounded-lg pl-4 pr-10 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500" data-id="element-300" />
          <button className="absolute right-2 p-1.5 text-gray-400 hover:text-teal-600 transition-colors" data-id="element-301">
            <Send size={16} className="transform rotate-45" data-id="element-302" />
          </button>
        </div>
        <div className="flex items-center justify-center mt-3 text-[10px] text-gray-500" data-id="element-303">
          <Info size={10} className="mr-1" data-id="element-304" />
          Copilot uses AI. Please double-check responses.
        </div>
      </div>
    </div>;
}